/**
 * @fileoverview Thin system-web adapter composing the shared StructuredAddressField
 *   for department/tenant address entry during onboarding and editing.
 * @module system-web/components/shared-forms
 *
 * Responsibilities:
 * - Map between the department address shape ({ street, zip, county, country })
 *   and PostalAddress ({ line1, postalCode, countryCode }) used by
 *   StructuredAddressField.
 * - Inject system-web's Mapbox token so callers don't repeat that plumbing.
 * - Keep the county field (not in PostalAddress) as a separate input alongside
 *   the structured address.
 *
 * Dependencies:
 * - @firesquirrel/ui-admin StructuredAddressField
 * - @firesquirrel/core-domain PostalAddress, WizardDepartmentAddress, adapters
 * - system-web config (getWebConfig)
 * - @ui-admin/contexts/ThemeContext (useTheme)
 *
 * Architecture:
 * - Presentation-only; delegates all network work (Mapbox, USPS) to
 *   StructuredAddressField's internal hook. No Dexie or direct fetch.
 * - Field-name mapping between WizardDepartmentAddress and PostalAddress is
 *   handled by shared adapters in core-domain/addressAdapters, not inlined here.
 *
 * Notes:
 * - A department's own address should be USPS-confirmed, so requireValidation
 *   defaults to true.
 */

import React, { useCallback, useMemo } from 'react';
import { StructuredAddressField } from '@firesquirrel/ui-admin';
import type { PostalAddress } from '@firesquirrel/core-domain';
import {
  wizardAddressToPostalAddress,
  postalAddressToWizardAddress,
} from '@firesquirrel/core-domain';
import type { WizardDepartmentAddress } from '@firesquirrel/core-domain';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import { getWebConfig } from '../../config';

const LABEL_CLS = 'block text-sm font-medium text-gray-700 mb-1';
const INPUT_CLS = 'w-full px-3 py-2 text-sm rounded-lg border focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none';
const ERROR_CLS = 'text-xs text-red-500 mt-0.5';

/** Re-export for backward compatibility — callers import DepartmentAddress from here. */
export type DepartmentAddress = WizardDepartmentAddress;

export interface DepartmentAddressFieldProps {
  /** Current department address value. */
  value: DepartmentAddress;
  /** Emits the updated address fields. */
  onChange: (next: DepartmentAddress) => void;
  /** Whether address inputs have validation errors (red border). */
  hasError?: boolean;
  /**
   * Whether to render the free-text County input alongside the structured
   * address. Defaults to true for backward compatibility (EditDepartmentModal).
   * Set to false when the caller provides a jurisdiction-sourced county
   * dropdown (e.g. the onboarding wizard), to avoid duplicate county fields.
   */
  showCounty?: boolean;
}

/**
 * @component DepartmentAddressField
 * Purpose:     Structured address entry for department/tenant records, with
 *              Mapbox-assisted typing and USPS validation.
 * Props:       value (DepartmentAddress), onChange, hasError.
 * Behavior:    Wraps StructuredAddressField with field-name mapping and a
 *              separate county input. Injects system-web's Mapbox token and
 *              theme automatically.
 * Data Source: hybrid (Mapbox + USPS via StructuredAddressField internals).
 * Constraints: Presentation-only; no direct API calls or Dexie access.
 * Performance: Re-renders only when value/onChange change; postal conversion
 *              memoized.
 */
export default function DepartmentAddressField({
  value,
  onChange,
  hasError,
  showCounty = true,
}: DepartmentAddressFieldProps) {
  const { theme } = useTheme();
  const mapboxToken = getWebConfig().mapboxToken;

  const postalValue = useMemo(
    () => wizardAddressToPostalAddress(value),
    [value],
  );

  const handlePostalChange = useCallback(
    (next: PostalAddress | undefined) => {
      onChange(postalAddressToWizardAddress(next, value.county));
    },
    [onChange, value.county],
  );

  const handleCountyChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...value, county: e.target.value || undefined });
    },
    [onChange, value],
  );

  const borderCls = hasError ? 'border-red-400 ring-1 ring-red-200' : '';

  return (
    <div className="space-y-3">
      <div className={borderCls ? `rounded-lg ${borderCls} p-0.5` : ''}>
        <StructuredAddressField
          theme={theme}
          value={postalValue}
          onChange={handlePostalChange}
          mapboxToken={mapboxToken}
          requireValidation
          labelCls={LABEL_CLS}
          inputCls={INPUT_CLS}
          errorCls={ERROR_CLS}
        />
      </div>
      {showCounty && (
        <div>
          <label className={LABEL_CLS}>County</label>
          <input
            type="text"
            value={value.county ?? ''}
            onChange={handleCountyChange}
            className={`${INPUT_CLS} border-gray-300`}
            placeholder="e.g. Maricopa"
            autoComplete="off"
          />
        </div>
      )}
    </div>
  );
}
